import React from 'react';
import { cn } from '../../utils/cn';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  isLoading?: boolean;
  children: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ 
    className, 
    variant = 'primary', 
    size = 'md', 
    isLoading = false, 
    disabled,
    children, 
    ...props 
  }, ref) => {
    const baseStyles = cn(
      'inline-flex items-center justify-center rounded-lg font-medium',
      'transition-all duration-200 ease-in-out',
      'focus:outline-none focus:ring-2 focus:ring-offset-2',
      'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
      'transform active:scale-[0.98]'
    );
    
    const variants = {
      primary: cn(
        'bg-primary-600 text-white shadow-professional',
        'hover:bg-primary-700 hover:shadow-medium',
        'focus:ring-primary-500'
      ),
      secondary: cn(
        'bg-white text-neutral-700 border border-neutral-300 shadow-professional',
        'hover:bg-neutral-50 hover:shadow-medium hover:border-neutral-400',
        'focus:ring-neutral-500'
      ),
      outline: cn(
        'border-2 border-primary-600 bg-transparent text-primary-600',
        'hover:bg-primary-50 hover:border-primary-700',
        'focus:ring-primary-500'
      ),
      ghost: cn(
        'text-neutral-700 bg-transparent',
        'hover:bg-neutral-100 hover:text-neutral-900',
        'focus:ring-neutral-500'
      ),
      danger: cn(
        'bg-error-600 text-white shadow-professional',
        'hover:bg-error-700 hover:shadow-medium',
        'focus:ring-error-500'
      ),
      success: cn(
        'bg-success-600 text-white shadow-professional',
        'hover:bg-success-700 hover:shadow-medium',
        'focus:ring-success-500'
      ),
    };

    const sizes = {
      sm: 'h-9 px-3 text-sm gap-1.5',
      md: 'h-10 px-4 text-sm gap-2',
      lg: 'h-11 px-6 text-base gap-2',
      xl: 'h-12 px-8 text-base gap-2.5',
    };

    return (
      <button
        className={cn(
          baseStyles,
          variants[variant],
          sizes[size],
          className
        )}
        ref={ref}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading && (
          <div className="spinner mr-2" />
        )}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
